import {copy_object, download_blob, is_nested_object, object_to_query_string, property_of} from '@bitstillery/common/lib/utils'
import {unchecked} from '@bitstillery/common/lib/unchecked'
import {$m, $s, config} from '@bitstillery/common/app'
import type {ErrorResponse} from '@bitstillery/common/api/fact2server'
import type {Body, Method, Result} from '@bitstillery/common/api/routes'
import {logger} from '@bitstillery/common/app'
import {notifier} from '@bitstillery/common/app'
import {current_account_slug} from '@bitstillery/common/lib/account'

interface ApiResultBase {
    response: Response // The raw response object
    status_code: number
    // Used in collection calls, number of items in the result. A GET without a JSON body (204, a file) has none.
    total: number | null | undefined
}

/** A call that succeeded (status < 300): result is the endpoint's body. */
export interface ApiSuccess<T> extends ApiResultBase {
    success: true
    result: T
    error_result?: undefined
}

/** A call that failed: result is whatever body came back (an error, or `{}` without one), so never T. */
export interface ApiFailure extends ApiResultBase {
    success: false
    result: unknown
    error_result?: ErrorResponse | undefined
}

/** Check `success` before reading `result`: only then is it the endpoint's body. */
export type ApiResult<T> = ApiSuccess<T> | ApiFailure

/** The `message` a failed v1 factserver call carries, or `fallback` when its body has none. */
export function failure_message(result: unknown, fallback = ''): string {
    return is_nested_object(result) && typeof result.message === 'string' && result.message ? result.message : fallback
}

/**
 * A failed v2 call's `detail` when it's a message. ErrorResponse types it as a string, but FastAPI's
 * validation errors (422) carry a list of field errors there instead.
 */
export function error_detail(error_result: unknown): string | undefined {
    const detail = property_of(error_result, 'detail')
    return typeof detail === 'string' ? detail : undefined
}

/**
 * The result as the request methods build it, with `success` as a plain boolean. A successful body is
 * taken to be what the endpoint declares: it's JSON from our own API, which nothing checks at runtime.
 */
function api_result<T>(
    fields: ApiResultBase & {success: boolean; result: unknown; error_result?: ErrorResponse | undefined},
): ApiResult<T> {
    const {error_result, result, success, ...base} = fields
    if (!success) {
        return {...base, error_result, result, success}
    }
    return {...base, result: unchecked<T>(result), success}
}

/** The body of a failed call, which fact2server sends as an ErrorResponse. */
function error_body(body: unknown): ErrorResponse {
    return unchecked<ErrorResponse>(body)
}

function disposition_file_name(response: Response): string | undefined {
    return response.headers.get('content-disposition')?.match(/filename="?([^";]+)"?/)?.[1]
}

/** A `total`/`total_count` field of a response body, which the API sends as a number or null. */
function count_field(value: unknown): number | null {
    return typeof value === 'number' ? value : null
}

/**
 * A call through a method picked at runtime (`api[method]`): a union of the generic method
 * signatures isn't callable, and a configured path can't select a route type anyway.
 */
export type DynamicRequest = <T>(endpoint: string, data?: object | null, v2?: boolean) => Promise<ApiResult<T>>

export interface FactServerErrorResponse {
    message: string
    type: string
}

/** Sees every GET response; answering false marks it as intercepted. */
type ApiHook = (request: {data: unknown; endpoint: string; result_data: unknown}, response: Response) => boolean
/** A POST hook sees the payload as sent, and which server it went to, instead of the result. */
type ApiPostHook = (request: {data: unknown; endpoint: string; v2: boolean}, response: Response) => boolean
interface ApiHooks {
    delete: ApiHook[]
    get: ApiHook[]
    post: ApiPostHook[]
    put: ApiHook[]
}

export default class Api {
    bodyMethods = ['POST', 'PUT']
    hooks: ApiHooks = {
        get: [],
        delete: [],
        post: [],
        put: [],
    }

    async call(endpoint: string, method: string, data: unknown, v2: boolean, signal?: AbortSignal) {
        const t0 = performance.now()
        const token = $s.identity.token

        const host = v2 ? config.api_host_new : config.api_host
        // Join host and endpoint with exactly one slash, regardless of trailing/leading slashes.
        const api_endpoint = `${host.replace(/\/+$/, '')}/${endpoint.replace(/^\/+/, '')}`

        let body: string | null = null
        if (this.bodyMethods.includes(method)) {
            body = JSON.stringify(data)
        }
        const headers = {
            Accept: 'application/json, text/*',
            'Accept-Encoding': 'gzip, deflate, br',
            'Content-Type': 'application/json; charset=UTF-8',
            'X-Requested-With': 'XMLHttpRequest',
            'X-transaction-id': `portal-${this.transaction_id()}`,
            'X-Auth-Token': token || '',
            'X-account-slug': current_account_slug(),
        }

        const request_data = {
            body,
            headers: headers,
            method,
            ...(signal === undefined ? {} : {signal}),
        }

        const request = new Request(api_endpoint, request_data)
        const response = await fetch(request)
        logger.debug(`[api] ${endpoint} (${Math.round(performance.now() - t0)}ms)`)
        return response
    }

    async delete<T = never, P extends string = string>(endpoint: P): Promise<ApiResult<Result<T, P, 'delete'>>> {
        const response = await this.call(endpoint, 'DELETE', null, true)
        const status_code = response.status
        const success = status_code < 300
        const content_type = response.headers.get('content-type')
        const is_json_content = content_type?.startsWith('application/json')
        // 204 No Content has no body; parsing would throw SyntaxError
        const result: unknown = is_json_content && response.status !== 204 ? await response.json() : undefined
        const error_result = !success ? error_body(result) : undefined

        if (response.status >= 500) {
            console.warn(`${response.status}: ${response.statusText} (${response.url})`)
            notifier.notify(`Server error: ${response.status}: ${response.statusText} (${response.url})`)
            return {
                response,
                result: {},
                status_code: response.status,
                success: false,
                total: 0,
            }
        }

        if (response.status === 401 && $s.identity.token) {
            $m.identity.logout_debounced()
        }

        return api_result<Result<T, P, 'delete'>>({
            response,
            result,
            error_result,
            status_code: response.status,
            success,
            total: 0,
        })
    }

    async head(endpoint: string): Promise<ApiResult<undefined>> {
        const response = await this.call(endpoint, 'HEAD', null, true)
        return {
            response,
            result: undefined,
            status_code: response.status,
            success: response.status < 300,
            total: 0,
        }
    }

    async get<T = never, P extends string = string>(
        endpoint: P,
        data: object | null = null,
    ): Promise<ApiResult<Result<T, P, 'get'>>> {
        let url: string = endpoint
        if (data) {
            if ('artkeys' in data && Array.isArray(data.artkeys)) {
                data.artkeys = data.artkeys.join(',')
            }
            const flat_data = object_to_query_string(copy_object(data))
            let query_string = new URLSearchParams(flat_data).toString()
            url = `${endpoint}?${query_string}`
        }
        const response = await this.call(url, 'GET', null, true)
        if (response.status >= 500) {
            console.warn(`${response.status}: ${response.statusText} (${response.url})`)
            notifier.notify(`${response.status}: ${response.statusText} (${response.url})`, 'danger', 0)
            return {
                response,
                result: {},
                status_code: response.status,
                success: false,
                total: 0,
            }
        }
        let success = true
        if (response.status === 401 && $s.identity.token) {
            $m.identity.logout_debounced()
        }

        if (response.status > 299 || response.status < 200) {
            success = false
        }

        const content_type = response.headers.get('content-type')

        let result_data: unknown, total: number | null | undefined
        const is_json_content = content_type?.startsWith('application/json')

        if (response.status !== 204) {
            if (!content_type || is_json_content) {
                const result: unknown = await response.json()
                if (is_nested_object(result)) {
                    total =
                        'total' in result
                            ? count_field(result.total)
                            : 'total_count' in result
                              ? count_field(result.total_count)
                              : null
                    // Deal with several response types from the API.
                    if ('result' in result) {
                        result_data = result.result
                    } else if ('items' in result) {
                        result_data = result.items
                    } else {
                        result_data = result
                    }
                } else {
                    total = null
                    result_data = result
                }
            } else {
                result_data = await response.blob()
            }
        }
        let error_result: ErrorResponse | undefined = undefined
        if (response.status > 299 && is_json_content && response.status < 500) {
            error_result = error_body(result_data)
        }

        // Hook into the response.
        if (!this.hooks.get.every((hook) => hook({endpoint: url, data, result_data}, response))) {
            return api_result<Result<T, P, 'get'>>({
                response,
                result: result_data,
                error_result: error_result,
                status_code: response.status,
                success,
                total,
            })
        }

        return api_result<Result<T, P, 'get'>>({
            response,
            result: result_data,
            error_result: error_result,
            status_code: response.status,
            success,
            total,
        })
    }

    /**
     * A call through a method picked at runtime, such as a collection's configured endpoint: a path
     * known only at runtime selects no route, so the body is unknown to the caller.
     */
    async request(method: Method, endpoint: string, data?: object | null, v2?: boolean): Promise<ApiResult<unknown>> {
        if (method === 'get') {
            return this.get(endpoint, data)
        }
        if (method === 'post') {
            return this.post(endpoint, data ?? undefined, v2)
        }
        if (method === 'put') {
            return this.put(endpoint, data ?? undefined)
        }
        return this.delete(endpoint)
    }

    load_hooks(hooks: Partial<ApiHooks>) {
        Object.assign(this.hooks, hooks)
    }

    /**
     * POST to fact2server (v2) and return the raw Response — for SSE or other streaming bodies.
     * Same auth headers as `post`; does not parse JSON.
     *
     * A body that streams is one the caller may want to walk away from, so this is the one call that
     * takes a signal: aborting it drops the connection, which is also what tells the server to stop
     * producing.
     */
    async postResponse(endpoint: string, data: object = {}, signal?: AbortSignal): Promise<Response> {
        return this.call(endpoint, 'POST', data, true, signal)
    }

    async post<T = never, P extends string = string, V extends boolean = boolean>(
        endpoint: P,
        // Only fact2server (v2) routes are in the generated types; a v1 body stays unchecked.
        data?: V extends true ? Body<P, 'post'> : object,
        v2_argument?: V,
    ): Promise<ApiResult<Result<T, P, 'post'>>> {
        const v2: boolean = v2_argument ?? false
        let payload: object = data ?? {}
        if (process.env.MSI_PACKAGE === 'discover' && !v2) {
            // Deal with legacy requests to the factserver (Discover)
            const v1_data_format = {
                data: {
                    _account_slug: current_account_slug(),
                },
            }

            Object.assign(v1_data_format.data, payload)
            payload = v1_data_format
        }
        const response = await this.call(endpoint, 'POST', payload, v2)
        const content_type = response.headers.get('content-type')
        const is_json_content = content_type?.startsWith('application/json')

        let result: unknown = null
        let error_result: ErrorResponse | undefined = undefined

        if (response.status >= 500) {
            console.warn(`${response.status}: ${response.statusText} (${response.url})`)
            notifier.notify(`${response.status}: ${response.statusText} (${response.url})`, 'danger', 0)
            return {
                response,
                result: {},
                error_result: error_result,
                status_code: response.status,
                success: false,
                total: 0,
            }
        }

        if (response.status !== 204) {
            if (!content_type || is_json_content) {
                result = await response.json()
                error_result = response.status > 299 ? error_body(result) : undefined
            } else {
                result = await response.blob()
            }
        }

        // Hook into the response.
        if (!this.hooks.post.every((hook) => hook({endpoint, data: payload, v2}, response))) {
            // If result is a blob, return it directly
            return api_result<Result<T, P, 'post'>>({
                result,
                error_result: error_result,
                success: response.status < 300,
                status_code: response.status,
                response,
                total: 0,
            })
        }

        if (response.status === 401) {
            if ($s.identity.token) {
                $m.identity.logout_debounced()
            }
            return {
                response,
                result: {},
                error_result: error_result,
                status_code: response.status,
                success: false,
                total: 0,
            }
        }

        let success = true
        let total: number = 0

        // (!) Make sure the response format for v1 & v2 is common
        let result_data: unknown = null

        // If result is a blob, return it directly without JSON processing
        if (result instanceof Blob) {
            result_data = result
            success = response.status < 300
        } else if (!result) {
            result = {}
        } else if (typeof result === 'object' && !Array.isArray(result)) {
            total =
                ('total' in result ? count_field(result.total) : 'total_count' in result ? count_field(result.total_count) : 0) ??
                0
        }

        if (!(result instanceof Blob)) {
            if (v2) {
                if (result && typeof result === 'object' && 'total' in result) {
                    delete result.total
                }
                success = response.status < 300
            } else {
                if (result && typeof result === 'object' && 'success' in result) {
                    success = Boolean(result.success)
                    delete result.success
                } else if (
                    result &&
                    typeof result === 'object' &&
                    'result' in result &&
                    typeof result.result === 'object' &&
                    result.result &&
                    'success' in result.result
                ) {
                    // Deal with inconsistent placement of 'success' in response
                    success = Boolean(result.result.success)
                    delete result.result.success
                }

                if (
                    typeof result !== 'boolean' &&
                    result !== null &&
                    !Array.isArray(result) &&
                    typeof result === 'object' &&
                    Object.keys(result).length === 0
                ) {
                    result = null
                }
            }

            if (result) {
                if (typeof result === 'object' && !Array.isArray(result)) {
                    if ('result' in result) {
                        result_data = result.result
                    } else if ('items' in result) {
                        result_data = result.items
                    } else {
                        result_data = result
                    }
                } else {
                    result_data = result
                }
            }
        }

        return api_result<Result<T, P, 'post'>>({
            response,
            result: result_data,
            error_result,
            status_code: response.status,
            success,
            total: total || 0,
        })
    }

    async put<T = never, P extends string = string>(endpoint: P, data?: Body<P, 'put'>): Promise<ApiResult<Result<T, P, 'put'>>> {
        const response = await this.call(endpoint, 'PUT', data ?? {}, true)
        const status_code = response.status
        const success = status_code < 300
        const content_type = response.headers.get('content-type')
        const is_json_content = content_type?.startsWith('application/json')

        const result: unknown = is_json_content ? await response.json() : undefined
        const error_result = !success ? error_body(result) : undefined

        if (response.status >= 500) {
            console.warn(`${response.status}: ${response.statusText} (${response.url})`)
            notifier.notify(`${response.status}: ${response.statusText} (${response.url})`, 'danger', 0)
            return api_result<Result<T, P, 'put'>>({
                response,
                result,
                error_result,
                status_code,
                success,
                total: 0,
            })
        }

        if (response.status === 401 && $s.identity.token) {
            $m.identity.logout_debounced()
        }

        const total = result && typeof result === 'object' && 'total' in result ? count_field(result.total) : 0
        if (result && typeof result === 'object' && 'total' in result) {
            delete result.total
        }

        return api_result<Result<T, P, 'put'>>({
            response,
            result,
            error_result,
            status_code,
            success,
            total,
        })
    }

    /** Saves the response body as a file, named after the server's content-disposition without a `file_name`. */
    async download(
        endpoint: string,
        file_name?: string,
        data: object | null = null,
        method: 'GET' | 'POST' | 'PUT' = 'GET',
    ): Promise<{status_code: number; success: boolean}> {
        const response = await this.call(endpoint, method, data ?? {}, true)
        const status_code = response.status
        if (status_code >= 500) {
            console.warn(`${status_code}: ${response.statusText} (${response.url})`)
            notifier.notify(`${status_code}: ${response.statusText} (${response.url})`, 'danger', 0)
        }
        if (status_code === 401 && $s.identity.token) {
            $m.identity.logout_debounced()
        }
        const success = status_code < 300 && status_code !== 204
        if (success) {
            download_blob(await response.blob(), file_name ?? disposition_file_name(response) ?? 'download')
        }
        return {status_code, success}
    }

    transaction_id() {
        return Math.random().toString(36).slice(2, 11)
    }
}
